<template>
  <nav
    role="navigation"
    :aria-label="$t('section__top')"
    :class="{ open: isOpen }"
    class="navigation-menu tw-flex tw-bg-transparent tw-fixed tw-left-0 tw-top-0 tw-min-w-[100vw] tw-max-w-[100vw] tw-group tw-z-30 xl:tw-min-w-[48px] xl:tw-max-w-[184px]"
  >
    <div
      class="tw-min-h-screen tw-pt-24 tw-bg-[#f8f8f8] tw-min-w-[284px] tw-max-w-[80vw] xl:tw-min-w-[48px] xl:tw-max-w-[184px] xl:tw-pt-5"
    >
      <nuxt-link
        class="tw-fixed tw-left-5 tw-hidden tw-z-30 xl:tw-block xl:group-hover:tw-hidden tw-pt-1.5 tw-pb-2.5 tw-rounded tw-shadow-md"
        :to="localePath('/')"
      >
        <img alt="Uptogether" class="" src="/logo_small.png" />
      </nuxt-link>

      <nuxt-link
        class="tw-hidden tw-h-[90px] tw-px-5 xl:tw-block xl:tw-mb-6"
        :aria-label="$t('go_home')"
        :to="localePath('/')"
        @click.native="$emit('close')"
      >
        <img
          alt="Uptogether"
          class="tw-object-contain navigation-logo xl:tw-hidden"
          src="/logo.png"
        />
      </nuxt-link>

      <div class="navigation-links tw-space-y-5 tw-text-center">
        <template v-if="$auth.loggedIn">
          <NuxtOrExternalLink
            v-for="(item, index) in filteredPages"
            :key="index"
            :to="item.to"
            :href="item.href"
            :class="{ 'xl:tw-justify-start xl:tw-pl-6 xl:tw-pr-11': isOpen }"
            :link-props="item.linkProps"
            class="tw-flex tw-items-center tw-justify-center navigation-link tw-overflow-hidden tw-text-3xl tw-text-[#4164e3] xl:tw-h-[38px] xl:tw-py-1.5 xl:tw-text-lg xl:tw-text-black xl:group-hover:tw-justify-start xl:group-hover:tw-pl-8 xl:hover:tw-text-white xl:hover:tw-bg-black"
            @click.native="onClickItem(item)"
          >
            <component
              :is="item.iconType"
              v-if="item.icon"
              class="!tw-hidden navigation-icon tw-h-[1em] tw-w-[1em] xl:!tw-flex"
              :src="
                item.iconType === 'img'
                  ? '/images/community_icon.png'
                  : undefined
              "
              :title="item.title"
              :alt="item.iconType === 'img' ? item.iconAlt : undefined"
            />
            <span class="navigation-title xl:tw-hidden">{{ item.title }}</span>
          </NuxtOrExternalLink>
        </template>

        <a
          class="tw-flex tw-items-center tw-justify-center tw-text-3xl tw-text-[#4164e3] xl:tw-hidden"
          @click="dropdownOpen = !dropdownOpen"
        >
          <ChevronDown v-if="!dropdownOpen" class="tw-mr-1" />
          <ChevronUp v-if="dropdownOpen" class="tw-mr-1" />
          {{ $i18n.localeProperties.name }}
        </a>
        <div v-if="dropdownOpen">
          <a
            v-for="locale in $i18n.locales"
            :key="locale.code"
            class="tw-block tw-px-3 tw-py-2 tw-text-center tw-text-[#4164e3]"
            @click="changeLanguage(locale)"
          >
            {{ locale.name }}
          </a>
        </div>
      </div>

      <div class="tw-text-center xl:tw-hidden">
        <button
          v-if="$auth.loggedIn"
          class="tw-border tw-mt-14 tw-px-8 tw-py-2 tw-rounded-lg signout-link tw-text-xs tw-uppercase"
          @click="$emit('signout')"
        >
          {{ $t('log_out') }}
        </button>
        <button
          v-if="!$auth.loggedIn"
          class="tw-border tw-mt-14 tw-px-8 tw-py-2 tw-rounded-lg signout-link tw-text-xs tw-uppercase"
          @click="doLogin"
        >
          {{ $t('log_in') }}
        </button>
      </div>
    </div>
  </nav>
</template>

<script>
import ChevronDown from 'vue-material-design-icons/MenuDown'
import ChevronUp from 'vue-material-design-icons/MenuUp'
import HomeIcon from 'vue-material-design-icons/Home'
import GroupsIcon from 'vue-material-design-icons/AccountMultiple'
import FinancialsIcon from 'vue-material-design-icons/Finance'
import SurveysIcon from 'vue-material-design-icons/ClipboardText'
import SettingsIcon from 'vue-material-design-icons/Cog'
import NuxtOrExternalLink from '@/components/shared/NuxtOrExternalLink'
import PermisionsMixin from '@/components/shared/PermisionsMixin'
import NavigationItemMixin from '@/components/shared/NavigationItemMixin'

export default {
  components: {
    ChevronDown,
    ChevronUp,
    HomeIcon,
    GroupsIcon,
    FinancialsIcon,
    SurveysIcon,
    SettingsIcon,
    NuxtOrExternalLink,
  },
  mixins: [PermisionsMixin, NavigationItemMixin],
  props: {
    isOpen: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      dropdownOpen: false,
      pages: [
        {
          iconType: 'HomeIcon',
          icon: 'HomeIcon',
          title: this.$t('navigation__home'),
          to: this.localePath('index'),
        },
        {
          iconType: 'GroupsIcon',
          icon: 'GroupsIcon',
          iconAlt: 'Groups icon',
          title: this.$tc('navigation__group', 2),
          href: process.env.COMMUNITY_SITE_URL,
          permissions: ['app_social'],
          analytics: {
            event: 'social__community_site',
            args: (item) => ({ site: item.href }),
          },
        },
        {
          iconType: 'FinancialsIcon',
          icon: 'FinancialsIcon',
          title: this.$t('navigation__financials'),
          to: this.localePath('financials'),
          permissions: ['app_financials'],
        },
        {
          iconType: 'SurveysIcon',
          icon: 'SurveysIcon',
          title: this.$tc('navigation__survey', 2),
          to: this.localePath('surveys'),
          permissions: ['app_surveys'],
        },
        {
          iconType: 'SettingsIcon',
          icon: 'SettingsIcon',
          title: this.$t('navigation__settings'),
          to: this.localePath('settings'),
        },
      ],
    }
  },
  computed: {
    filteredPages() {
      return this.pages.filter((page) => this.hasPermisionAndFeature(page))
    },
  },
  watch: {
    isOpen(val) {
      if (val) {
        document.documentElement.classList.add('tw-overflow-hidden')
      } else {
        document.documentElement.classList.remove('tw-overflow-hidden')
      }
    },
  },
  methods: {
    changeLanguage(locale) {
      this.dropdownOpen = false

      const questions = this.$store.state.surveyQuestions.questionsWithAnswers
      const question = questions.find(
        ({ code }) => code === 'language_preference'
      )
      if (question) {
        // Send to the server the language preference
        const data = {
          ...question,
          value: {
            display: locale.name,
            type: 'string',
            value: locale.code,
          },
        }

        this.$store.dispatch('surveyQuestions/save', {
          questions: [data],
          withAnswers: true,
          skipInstructions: true,
        })
      }

      if (
        this.$router.currentRoute.fullPath === `/${this.$i18n.locale}` &&
        locale.code === 'en'
      ) {
        this.$router.push('/')
      } else {
        this.$i18n.setLocale(locale.code)
      }
    },
    doLogin() {
      const encodedLoc = encodeURIComponent(window.location.href)
      const loginUrl = `${process.env.AUTH_URL}?redirect_url=${encodedLoc}`
      window.location.href = loginUrl
    },
    onClickItem(item) {
      this.sendEventFromAnalyticsDefs(item)
      this.$emit('close')
    },
  },
}
</script>

<style scoped lang="scss">
.navigation-menu {
  transform-origin: 0% 0%;
  transform: translate(-100%, 0);
  transition: transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1);

  &.open {
    transform: none;
  }

  @media (min-width: 1280px) {
    transform: none;

    &:hover {
      .navigation-logo {
        display: block;
      }

      .navigation-icon {
        margin-right: 0.375rem;
      }

      .navigation-title {
        display: block !important;
      }
    }
  }
}

.navigation-links {
  .nuxt-link-exact-active {
    @media (min-width: 1280px) {
      background-color: #000;
      color: #fff !important;
      img {
        filter: invert(1);
      }
    }
  }

  .navigation-link {
    @media (min-width: 1280px) {
      &:hover {
        img {
          filter: invert(1);
        }
      }
    }
  }
}

.signout-link {
  border-color: #ee2828;
  color: #ee2828;
}
</style>
